import posthog from 'posthog-js';
import { ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useConfig } from '../config';
import { fromAccounts, fromAuth } from '../store';
import {
  alias,
  AnalyticsProperties,
  clearSegmentIdentity,
  identifySegmentAccount,
  identifySegmentUser,
  normalizeAnonymousId,
} from './segment';

export {
  alias,
  identifySegmentAccount,
  identifySegmentUser,
  normalizeAnonymousId,
};

let initializedKey: string | undefined;
let initializedHost: string | undefined;
let lastDistinctId: string | undefined;

const postHogIdentifyBlockedProperties = new Set([
  'first_name',
  'last_name',
  'firstName',
  'lastName',
  'account_name',
]);

const getPostHogApiKey = (configApiKey?: string) => (
  configApiKey || process.env.REACT_APP_POSTHOG_API_KEY || ''
);

const getPostHogHost = (configHost?: string) => (
  configHost || process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com'
);

const removePostHogIdentifyPii = (properties?: AnalyticsProperties) => {
  if (!properties) {
    return properties;
  }

  return Object.fromEntries(
    Object.entries(properties).filter(([key]) => !postHogIdentifyBlockedProperties.has(key)),
  );
};

export const initPostHog = (apiKey?: string, host?: string) => {
  if (!apiKey || (initializedKey === apiKey && initializedHost === host)) {
    return;
  }

  posthog.init(apiKey, {
    api_host: host,
    capture_pageview: false,
    autocapture: false,
  });
  initializedKey = apiKey;
  initializedHost = host;
};

export const page = (...args: any[]) => {
  window.analytics?.page?.(...args);

  if (!initializedKey) {
    return;
  }

  const maybeProperties = args.find(arg => arg && typeof arg === 'object' && !Array.isArray(arg));
  posthog.capture('$pageview', {
    ...(maybeProperties ?? {}),
    $current_url: window.location.href,
    path: window.location.pathname,
    title: document.title,
  });
};

export const track = (eventName: string, properties?: AnalyticsProperties) => {
  window.analytics?.track?.(eventName, properties);

  if (!initializedKey) {
    return;
  }

  posthog.capture(eventName, properties);
};

/** PostHog-only identify (Segment identify lives in `identifySegmentUser`). */
export const identify = (distinctId?: string, properties?: AnalyticsProperties) => {
  if (!initializedKey || !distinctId) {
    return;
  }

  if (lastDistinctId && lastDistinctId !== distinctId) {
    posthog.reset();
  }

  posthog.identify(distinctId, removePostHogIdentifyPii(properties));
  lastDistinctId = distinctId;
};

const identifyAccount = (accountId?: number, properties?: AnalyticsProperties) => {
  if (!initializedKey || !accountId) {
    return;
  }

  posthog.group('account', String(accountId), properties);
};

const resetPostHogIdentity = () => {
  if (!initializedKey || !lastDistinctId) {
    return;
  }

  posthog.reset();
  lastDistinctId = undefined;
};

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const { config } = useConfig();
  const user = useSelector(fromAuth.selectUser);
  // Explicit selection only (#1192): `selectSelectedAccount` falls back to `allAccounts[0]`,
  // which attributed pre-selection events to the wrong account for multi-account users.
  const account = useSelector(fromAccounts.selectExplicitlySelectedAccount);

  useEffect(() => {
    initPostHog(
      getPostHogApiKey(config?.posthogApiKey),
      getPostHogHost(config?.posthogHost),
    );
  }, [config?.posthogApiKey, config?.posthogHost]);

  useEffect(() => {
    if (!user) {
      resetPostHogIdentity();
      // Only forget the dedupe here. The browser-side Segment reset happens in `doLogout`
      // (see ./reset.ts); a user cleared by a failed token refresh must not rotate the
      // anonymous id.
      clearSegmentIdentity();
      return;
    }

    // Segment: userId is the e-mail, matching what TestOps (K1 UI + track proxy) already
    // sends, so both apps resolve to the same profile. Email stays a trait too so Unify
    // can merge on it.
    identifySegmentUser(user.email, {
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      name: [user.firstName, user.lastName].filter(Boolean).join(' ') || undefined,
      user_id: user.id,
    });

    identify(String(user.id), {
      user_id: user.id,
      email: user.email,
      system_role: user.roles,
      account_id: account?.id,
    });
  }, [user, account?.id, account?.name, config?.posthogApiKey, config?.posthogHost]);

  useEffect(() => {
    const accountTraits = {
      name: account?.name,
      account_id: account?.id,
      account_uuid: account?.uuid,
    };
    // Segment: account-level attribution (Unify group), same traits as the PostHog group.
    identifySegmentAccount(account?.id, accountTraits);
    identifyAccount(account?.id, accountTraits);
  }, [account?.id, account?.name, account?.uuid, config?.posthogApiKey, config?.posthogHost]);

  return <>{children}</>;
};
